/**
 * MOCK WORKER — Setup MSW (Mock Service Worker) per il dev mode.
 *
 * Attivato solo quando VITE_USE_MOCKS=true. Aggrega gli handler del core
 * e quelli contribuiti dai plugin tramite PluginManifest.getMockHandlers(),
 * poi avvia il Service Worker che intercetta tutte le chiamate fetch
 * a livello di rete browser.
 *
 * Modalità mock ibrida: VITE_USE_MOCK_AUTH (default 'true') controlla
 * se registrare gli handler auth. Quando 'false', le chiamate
 * /o/crm/v1_0/user/* passano al Vite proxy -> Liferay reale, mentre
 * gli handler dati restano mockati. Utile per testare il login vero
 * contro Liferay senza aver ancora implementato il backend REST delle entità.
 *
 * Il Service Worker (`public/mockServiceWorker.js`) deve essere stato
 * generato con `pnpm exec msw init public/ --save`. Vite serve public/
 * come root statico, quindi il file è raggiungibile a /mockServiceWorker.js.
 */

import { setupWorker } from 'msw/browser';
import { http, passthrough, type RequestHandler } from 'msw';
import { coreAuthHandlers, coreDataHandlers } from './mockHandlers';
import { getPlugins } from '../plugin/registry';

/**
 * Prefisso dell'API CRM, per riconoscere le chiamate che un mock dovrebbe intercettare.
 *
 * Terza copia dello stesso valore, e va tenuta allineata alle altre due: `API` in `mockHandlers.ts`
 * (le rotte mockate) e `CRM_BASE_URL` in `config.ts` (il baseURL vero). Resta una costante
 * letterale invece di un import di `CRM_BASE_URL` perché quel valore può essere un URL assoluto,
 * mentre questo confronto è su un pathname.
 */
const CRM_API_PATH_PREFIX = '/o/crm/v1_0/';

/** Stile del warning in console, lo stesso dell'avviso di mock attivo. */
const WARNING_STYLE = 'background: #f0ad4e; color: #000; padding: 2px 6px; font-weight: bold;';

/**
 * Chiamate già segnalate, per non ripetere lo stesso warning a ogni render o a ogni polling.
 * Vive quanto la pagina: un reload ridà il primo avviso di ciascuna.
 */
const missingMockReported = new Set<string>();

/**
 * Catch-all: per qualsiasi URL che non matcha un handler specifico, dice
 * esplicitamente a MSW di passare la richiesta al vero network (passthrough).
 * Questo evita che il Service Worker tenti un passthrough automatico per
 * navigazioni SPA (es. GET /login da hard reload) che falliscono in modo
 * inconsistente con "TypeError: Failed to fetch".
 *
 * Va messo come ULTIMO handler perché matcha qualsiasi URL.
 *
 * PORTA ANCHE IL DIAGNOSTICO dei mock mancanti, e deve portarlo lui. L'opzione
 * `onUnhandledRequest` di MSW non può servire allo scopo finché questo handler esiste: scatta solo
 * quando NESSUN handler matcha (in `msw/lib/core/utils/handleRequest.js`, il ramo
 * `if (!lookupResult)`), e un catch-all matcha sempre. Le due configurazioni insieme si annullano,
 * e sembrano entrambe attive: è così che sei endpoint delle automazioni sono rimasti senza mock
 * senza che nessuno vedesse mai il warning che li avrebbe denunciati (audit del 2026-09-21, §D.1
 * di ANALISI-TS-65-motore-asincrono.md). Qui invece l'URL che sta passando lo conosciamo.
 *
 * Regola generale, oltre MSW: un handler che matcha tutto disattiva ogni diagnostica fondata sul
 * «non ha matchato niente».
 */
const catchAllPassthrough: RequestHandler[] = [
  http.all(/.*/, ({ request }) => {
    const { pathname } = new URL(request.url);

    // Solo l'API CRM: navigazioni SPA, statici di Vite, HMR e chunk JS arrivano fin qui e devono
    // passare in silenzio, perché per loro il passthrough è il comportamento giusto, non un buco.
    if (pathname.startsWith(CRM_API_PATH_PREFIX)) {
      const key = `${request.method} ${pathname}`;

      if (!missingMockReported.has(key)) {
        missingMockReported.add(key);
        console.warn(
          `%c[Mock Worker] MOCK MANCANTE — ${key} non ha nessun handler e passa al proxy: in dev questa chiamata parla con il backend vero, oppure fallisce se non c'è.`,
          WARNING_STYLE,
        );
      }
    }

    return passthrough();
  }),
];

export async function startMockWorker(): Promise<void> {
  if (import.meta.env.VITE_USE_MOCKS !== 'true') return;

  // Modalità ibrida: VITE_USE_MOCK_AUTH=false -> auth reale contro Liferay,
  // dati ancora mockati. Default 'true' = comportamento storico (tutto mock).
  const mockAuth = import.meta.env.VITE_USE_MOCK_AUTH !== 'false';
  const coreHandlers = mockAuth
    ? [...coreAuthHandlers, ...coreDataHandlers]
    : coreDataHandlers;

  // Aggrega handler del core + handler dei plugin registrati
  const pluginHandlers: RequestHandler[] = [];
  for (const plugin of getPlugins()) {
    if (plugin.getMockHandlers) {
      const handlers = plugin.getMockHandlers();
      pluginHandlers.push(...handlers);
      console.info(`[Mock Worker] Plugin "${plugin.id}" ha contribuito ${handlers.length} handler.`);
    }
  }

  // Ordine importante: core handler -> plugin handler -> catch-all passthrough.
  // MSW processa gli handler in ordine: il primo che matcha vince.
  const worker = setupWorker(...coreHandlers, ...pluginHandlers, ...catchAllPassthrough);

  // Nessun `onUnhandledRequest`: con il catch-all in fondo non esiste una richiesta non gestita, e
  // configurarlo qui darebbe l'impressione di un controllo che non può scattare. Il diagnostico dei
  // mock mancanti sta dentro `catchAllPassthrough`, dove il suo comportamento è osservabile.
  await worker.start();

  // Warning proattivo in console: qualunque mock attivo viene segnalato
  // a un livello visibile (console.warn con stile) per evitare che uno
  // sviluppatore dimentichi di essere in mock mode e attribuisca ai mock
  // problemi che in realtà vengono dal backend (o viceversa).
  // Nessun warning quando VITE_USE_MOCKS=false (la funzione esce prima).
  if (mockAuth) {
    console.warn(
      `%c[Mock Worker] MOCK ATTIVO (auth + dati) — login, refresh, logout e tutte le entità sono intercettati da MSW. ${coreHandlers.length} core handler + ${pluginHandlers.length} plugin handler.`,
      WARNING_STYLE
    );
  } else {
    console.warn(
      `%c[Mock Worker] MOCK IBRIDO — auth REALE contro Liferay via Vite proxy, dati ancora mockati (${coreHandlers.length} core handler + ${pluginHandlers.length} plugin handler). Attenzione: il userId del login reale potrebbe non matchare gli id hardcoded nei mock.`,
      WARNING_STYLE
    );
  }
}
